/**
 * همان دادهٔ دمو، به انگلیسی.
 *
 * ═══ چرا «رویه» و نه یک فایلِ دومِ کامل ═══
 *
 * وسوسه‌ی اول این بود که `fixtures.ts` را کپی کنیم و متن‌هایش را ترجمه.
 * ولی آن‌وقت دو فایلِ ۲۸۰ خطی داشتیم که فقط در رشته‌ها فرق دارند، و هر
 * تغییری در ساختار — یک فیلدِ تازه، یک سیگنالِ تازه، یک شمارشِ عوض‌شده —
 * باید در هر دو انجام می‌شد. یکی‌شان زودتر یا دیرتر عقب می‌ماند.
 *
 * پس ساختار یکی می‌ماند و این فایل فقط **متن‌های دیدنی** را رویش می‌گذارد،
 * با کلیدِ شناسه‌ی پایدار (`s1`, `demo-sara`, `evt-2`, …). یک فیلدِ تازه در
 * `fixtures.ts` خودبه‌خود به این‌جا هم می‌رسد؛ فقط یک **متنِ** تازه است که
 * ترجمه می‌خواهد — و `fixturesEn.test.ts` دقیقاً همان را می‌گیرد: اگر پس از
 * ترجمه هنوز حرفِ فارسی‌ای مانده باشد، تست می‌شکند.
 *
 * ═══ چه چیزی ترجمه نمی‌شود ═══
 *
 * اسمِ جاهای واقعی («Gatineau Park»، «ByWard Market») از اول انگلیسی‌اند و
 * دست نمی‌خورند. `username`ها هم همین‌طور.
 */
import * as F from './fixtures.ts';
import type {
  AppNotification, AvailabilityCell, Business, EventItem, Friend, Group, ImportedContact,
  Message, NotifPrefs, Plan, Profile, Recipient, Signal, Tag,
} from '../domain/types.ts';

/* ───────────────────────── آدم‌ها ───────────────────────── */

const NAMES: Record<string, string> = {
  'demo-me': 'Parviz (demo)',
  'demo-sara': 'Sara',
  'demo-arash': 'Arash',
  'demo-negar': 'Negar',
  'demo-kian': 'Kian',
  'demo-maryam': 'Maryam',
};

/** یک پروفایل با نام و شهرِ انگلیسی — برای هر جایی که پروفایل تودرتو نشسته. */
const enProfile = <T extends Profile>(p: T): T => ({
  ...p,
  displayName: NAMES[p.id] ?? p.displayName,
  city: p.city ? 'Ottawa' : p.city,
  country: p.country ? 'Canada' : p.country,
});

export const DEMO_PROFILE: Profile = {
  ...enProfile(F.DEMO_PROFILE),
  statusText: 'Just showing the demo 👀',
};
export const SARA = enProfile(F.SARA);
export const ARASH = enProfile(F.ARASH);
export const NEGAR = enProfile(F.NEGAR);
export const KIAN = enProfile(F.KIAN);
export const MARYAM = enProfile(F.MARYAM);

/** پروفایلِ درستِ انگلیسیِ هر شناسه — شاملِ خودِ کاربرِ دمو، که statusText هم دارد. */
const profileOf = (p: Profile): Profile =>
  (p.id === F.DEMO_PROFILE.id ? DEMO_PROFILE : enProfile(p));

/* ───────────────────────── متن‌ها ───────────────────────── */

interface SignalText { title: string; note?: string; approxLabel?: string; placeName?: string }

const SIGNALS: Record<string, SignalText> = {
  s1: { title: 'Gatineau hike 🥾', note: 'Early start so we actually get parking' },
  s2: {
    title: 'Dinner at my place 🍲',
    note: 'I take the stew extremely seriously',
    approxLabel: 'This weekend',
  },
  s3: { title: 'Movie 🎬 if anyone is up for it' },
  s4: { title: 'Morning coffee ☕' },
  s5: { title: 'Who is up for walking the loop? 🚶' },
  s6: { title: 'Which restaurant? 🍜', approxLabel: 'Friday night' },
  s7: { title: "Negar's birthday 🎂", placeName: "Negar's place" },
};

/** پیام‌های گفت‌وگوی سیگنالِ s4، به همان ترتیبِ فیکسچر. */
const MESSAGES = [
  'Sara confirmed the plan.',
  "I'll book the cafe, don't worry 😊",
  'Great, see you there!',
];

/** تنها دو اعلانی که متنشان عنوانِ یک سیگنال است. */
const NOTIF_BODIES: Record<number, string> = {
  1: SIGNALS.s1.title,
  3: SIGNALS.s3.title,
};

/** و تنها اعلانی که عنوانش اسمِ آدم نیست. */
const CONTACT_JOINED = 'Someone from your contacts joined Pals & Plans';

interface ContactText {
  displayName: string;
  firstName: string | null;
  company?: string;
  relLabel?: string;
}

const CONTACTS: Record<number, ContactText> = {
  1: { displayName: 'Sara', firstName: 'Sara' },
  2: { displayName: 'Arash', firstName: 'Arash' },
  3: { displayName: 'Negar', firstName: 'Negar' },
  4: { displayName: 'Mohammad (old friend)', firstName: 'Mohammad' },
  5: { displayName: 'Elnaz (coworker)', firstName: 'Elnaz', company: 'Example Inc.' },
  6: { displayName: 'Uncle Reza', firstName: 'Reza', relLabel: 'Uncle' },
  7: { displayName: 'Samira (neighbour)', firstName: 'Samira' },
  8: { displayName: 'Pouya Gym', firstName: null },
};

const GROUPS: Record<number, string> = { 1: 'Hiking crew 🥾' };

interface EventText { title: string; description: string; priceNote?: string }

const EVENTS: Record<string, EventText> = {
  'evt-1': { title: "Concert at Major's Hill 🎵", description: 'Free live music under an open sky' },
  'evt-2': { title: 'ByWard farmers market 🧺', description: 'Local produce and handmade crafts' },
  'evt-3': {
    title: 'Board game night 🎲',
    description: 'Board games and new faces',
    priceNote: '$10 at the door',
  },
};

interface BusinessText { name: string; description: string; discountNote?: string }

const BUSINESSES: Record<string, BusinessText> = {
  'biz-1': {
    name: 'The Corner Cafe ☕',
    description: 'Specialty coffee and a quiet room',
    discountNote: '10% off for Pals & Plans friends',
  },
  'biz-2': { name: 'City Noodle House 🍜', description: 'Hand-pulled noodles and hot soup' },
  'biz-3': { name: 'Ottawa Bowling Club 🎳', description: 'Made for a group night out' },
};

/* ───────────────────── همان توابع، ترجمه‌شده ───────────────────── */

export function demoFriends(): Friend[] {
  return F.demoFriends().map((f) => ({ ...f, profile: profileOf(f.profile) }));
}

export function demoSignals(): Signal[] {
  return F.demoSignals().map((s) => {
    const t = SIGNALS[s.id];
    if (!t) return { ...s, creator: s.creator ? profileOf(s.creator) : s.creator };
    return {
      ...s,
      creator: s.creator ? profileOf(s.creator) : s.creator,
      title: t.title,
      // سیگنالی که در ترجمه یادداشت ندارد یعنی در اصل هم نداشته — نه اینکه
      // یادداشتش جا افتاده. پس `undefined` می‌شود، نه رشته‌ی فارسیِ باقی‌مانده.
      note: t.note,
      approxLabel: t.approxLabel,
      placeName: t.placeName ?? s.placeName,
    };
  });
}

export function demoRecipients(): Record<string, Recipient[]> {
  return Object.fromEntries(Object.entries(F.demoRecipients()).map(
    ([k, rs]) => [k, rs.map((r) => ({ ...r, profile: r.profile ? profileOf(r.profile) : r.profile }))],
  ));
}

export function demoPlans(): Record<string, Plan> {
  return F.demoPlans();
}

export function demoMessages(): Record<string, Message[]> {
  return Object.fromEntries(Object.entries(F.demoMessages()).map(
    ([k, ms]) => [k, ms.map((m, i) => ({
      ...m,
      body: MESSAGES[i] ?? m.body,
      senderName: (m.senderId ? NAMES[m.senderId] : undefined) ?? m.senderName,
    }))],
  ));
}

/**
 * عنوانِ بیشترِ اعلان‌ها اسمِ یک آدم است ولی شناسه‌اش را حمل نمی‌کنند، پس از
 * روی همان اسمِ اصلی به شناسه می‌رسیم. اگر روزی این نگاشت از کار بیفتد،
 * تستِ «هیچ حرفِ فارسی نماند» صدایش را درمی‌آورد.
 */
function nameIdOf(title: string | null): string {
  for (const p of [F.DEMO_PROFILE, F.SARA, F.ARASH, F.NEGAR, F.KIAN, F.MARYAM]) {
    if (p.displayName === title) return p.id;
  }
  return '';
}

export function demoNotifications(): AppNotification[] {
  return F.demoNotifications().map((n) => ({
    ...n,
    title: n.kind === 'contact_joined' ? CONTACT_JOINED : NAMES[nameIdOf(n.title)] ?? n.title,
    body: NOTIF_BODIES[n.id] ?? n.body,
  }));
}

export function demoContacts(): ImportedContact[] {
  return F.demoContacts().map((c) => {
    const t = CONTACTS[c.id];
    if (!t) return { ...c, matched: c.matched ? profileOf(c.matched) : c.matched };
    return {
      ...c,
      displayName: t.displayName,
      firstName: t.firstName,
      company: t.company ?? null,
      relationships: c.relationships.map((r) => ({ ...r, label: t.relLabel ?? r.label })),
      matched: c.matched ? profileOf(c.matched) : c.matched,
    };
  });
}

export function demoGroups(): Group[] {
  return F.demoGroups().map((g) => ({ ...g, name: GROUPS[g.id] ?? g.name }));
}

/**
 * برچسبِ فارسیِ تگ‌ها در رابطِ انگلیسی اصلاً خوانده نمی‌شود — رابط `labelEn`
 * را می‌گیرد. ولی تستِ «هیچ فارسی‌ای نماند» کلِ شیء را می‌گردد، و درست هم
 * می‌کند: یک برچسبِ فارسیِ ناخوانده امروز بی‌ضرر است و فردا در یک صفحه‌ی
 * تازه ظاهر می‌شود.
 */
export function demoTags(): Tag[] {
  return F.demoTags().map((t) => ({ ...t, labelFa: t.labelEn }));
}

export function demoEvents(): EventItem[] {
  return F.demoEvents().map((e) => {
    const t = EVENTS[e.id];
    if (!t) return e;
    return { ...e, title: t.title, description: t.description, priceNote: t.priceNote ?? null };
  });
}

export function demoBusinesses(): Business[] {
  return F.demoBusinesses().map((b) => {
    const t = BUSINESSES[b.id];
    if (!t) return b;
    return { ...b, name: t.name, description: t.description, discountNote: t.discountNote ?? null };
  });
}

export function demoAvailability(): AvailabilityCell[] {
  return F.demoAvailability();
}

export const DEMO_NOTIF_PREFS: NotifPrefs = F.DEMO_NOTIF_PREFS;
